import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useOutletContext } from 'react-router-dom';
import { BookOpen, Copy, Check, Search, Brain } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { getSessions } from '../../lib/coaching-sessions';
import type { CoachingSession, SessionAnalysis } from '../../lib/coaching-sessions';

interface OutletCtx { isUnlocked: boolean; }

interface QuestionEntry {
  question: string;
  clientName: string;
  sessionDate: string;
  category: string;
}

interface QuestionCategory {
  label: string;
  color: string;
  questions: QuestionEntry[];
}

const STATIC_CATEGORIES = [
  {
    label: 'Meta Model Questions',
    color: 'bg-blue-50 text-blue-700',
    questions: [
      '"When you say you can\'t, what specifically stops you?"',
      '"How do you know that\'s true?"',
      '"Who says that has to happen?"',
      '"What would happen if you did?"',
    ],
  },
  {
    label: 'Limiting Belief Questions',
    color: 'bg-orange-50 text-orange-700',
    questions: [
      '"What would need to be true for this to feel safe enough to begin?"',
      '"What are you protecting by staying where you are?"',
      '"What would change if confidence became a skill instead of an identity?"',
    ],
  },
  {
    label: 'Parts Conflict Questions',
    color: 'bg-teal-10 text-teal-50',
    questions: [
      '"What part of you wants this change, and what part of you resists it?"',
      '"If the part that resists had a positive intention, what would it be?"',
      '"What would it take for those two parts to find an agreement?"',
    ],
  },
  {
    label: 'Identity Questions',
    color: 'bg-success-10 text-success-50',
    questions: [
      '"Who would you need to become to achieve this?"',
      '"What would that version of you do differently?"',
      '"What identity are you holding onto that might be keeping you safe but stuck?"',
    ],
  },
  {
    label: 'Values Questions',
    color: 'bg-brand-10 text-brand-60',
    questions: [
      '"What value is being honoured by this behaviour?"',
      '"What value is being sacrificed?"',
      '"What matters most to you in this situation?"',
    ],
  },
  {
    label: 'Emotional Exploration Questions',
    color: 'bg-red-50 text-red-600',
    questions: [
      '"Where do you feel that in your body?"',
      '"What does this emotion want you to know?"',
      '"If this feeling could speak, what would it say?"',
    ],
  },
  {
    label: 'Action & Progress Questions',
    color: 'bg-gray-10 text-gray-60',
    questions: [
      '"What\'s one small step you could take this week?"',
      '"What would progress look like?"',
      '"What has changed since we last spoke?"',
    ],
  },
  {
    label: 'Breakthrough Zone Questions',
    color: 'bg-success-10 text-success-70',
    questions: [
      '"What do you believe would happen if you became more visible?"',
      '"Whose judgement are you most concerned about?"',
      '"What would it mean about you if this didn\'t work out?"',
    ],
  },
];

function extractQuestionsFromAnalysis(
  analysis: SessionAnalysis,
  clientName: string,
  sessionDate: string
): QuestionEntry[] {
  const entries: QuestionEntry[] = [];

  // Suggested coach questions (top-level)
  if (analysis.suggested_coach_questions?.length) {
    for (const q of analysis.suggested_coach_questions) {
      if (q?.trim()) {
        entries.push({ question: q.trim(), clientName, sessionDate, category: 'Suggested Questions' });
      }
    }
  }

  // Meta model questions
  if (analysis.meta_model_patterns?.length) {
    for (const m of analysis.meta_model_patterns) {
      if (m.coach_question?.trim()) {
        entries.push({ question: m.coach_question.trim(), clientName, sessionDate, category: 'Meta Model Questions' });
      }
    }
  }

  // Limiting belief explorations
  if (analysis.limiting_beliefs?.length) {
    for (const b of analysis.limiting_beliefs) {
      if (b.coaching_exploration?.trim()) {
        entries.push({ question: b.coaching_exploration.trim(), clientName, sessionDate, category: 'Limiting Belief Questions' });
      }
    }
  }

  // Parts conflict explorations
  if (analysis.parts_conflicts?.length) {
    for (const p of analysis.parts_conflicts) {
      if (p.coaching_exploration?.trim()) {
        entries.push({ question: p.coaching_exploration.trim(), clientName, sessionDate, category: 'Parts Conflict Questions' });
      }
    }
  }

  // Values questions
  if (analysis.values_and_motivation_clues?.length) {
    for (const v of analysis.values_and_motivation_clues) {
      if (v.coach_question?.trim()) {
        entries.push({ question: v.coach_question.trim(), clientName, sessionDate, category: 'Values Questions' });
      }
    }
  }

  // Avoidance explorations
  if (analysis.avoidance_or_resistance_patterns?.length) {
    for (const a of analysis.avoidance_or_resistance_patterns) {
      if (a.coach_exploration?.trim()) {
        entries.push({ question: a.coach_exploration.trim(), clientName, sessionDate, category: 'Avoidance & Resistance Questions' });
      }
    }
  }

  // Next session prep questions
  if (analysis.next_session_prep?.questions_to_ask?.length) {
    for (const q of analysis.next_session_prep.questions_to_ask) {
      if (q?.trim()) {
        entries.push({ question: q.trim(), clientName, sessionDate, category: 'Next Session Questions' });
      }
    }
  }

  return entries;
}

function groupByCategory(entries: QuestionEntry[]): QuestionCategory[] {
  const map = new Map<string, QuestionEntry[]>();
  for (const e of entries) {
    if (!map.has(e.category)) map.set(e.category, []);
    map.get(e.category)!.push(e);
  }

  const CATEGORY_COLORS: Record<string, string> = {
    'Suggested Questions': 'bg-success-10 text-success-60',
    'Meta Model Questions': 'bg-blue-50 text-blue-700',
    'Limiting Belief Questions': 'bg-orange-50 text-orange-700',
    'Parts Conflict Questions': 'bg-teal-10 text-teal-50',
    'Values Questions': 'bg-brand-10 text-brand-60',
    'Avoidance & Resistance Questions': 'bg-red-50 text-red-600',
    'Next Session Questions': 'bg-gray-10 text-gray-60',
    'Identity Questions': 'bg-success-10 text-success-50',
  };

  return Array.from(map.entries()).map(([label, questions]) => ({
    label,
    color: CATEGORY_COLORS[label] || 'bg-gray-10 text-gray-60',
    questions,
  }));
}

function QuestionItem({ question, clientName, sessionDate, isUnlocked }: {
  question: string;
  clientName?: string;
  sessionDate?: string;
  isUnlocked: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const text = question.startsWith('"') ? question : `"${question}"`;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <li className="group flex items-start justify-between gap-3 py-2">
      <div className="flex items-start gap-2.5 min-w-0">
        <div className="w-1.5 h-1.5 rounded-full bg-success-40 mt-2 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-sm text-gray-60 italic leading-relaxed">
            {question.startsWith('"') ? question : `"${question}"`}
          </p>
          {isUnlocked && clientName && (
            <p className="text-xs text-gray-40 mt-0.5">
              {clientName} · {new Date(sessionDate!).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            </p>
          )}
        </div>
      </div>
      {isUnlocked && (
        <button
          onClick={handleCopy}
          className="opacity-0 group-hover:opacity-100 flex-shrink-0 p-1.5 rounded-lg hover:bg-gray-10 text-gray-40 hover:text-gray-60 transition-all"
          title="Copy question"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-success-50" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      )}
    </li>
  );
}

export function SIQuestionBankPage() {
  const { isUnlocked } = useOutletContext<OutletCtx>();

  const [allEntries, setAllEntries] = useState<QuestionEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!isUnlocked) return;
    setLoading(true);
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: coach } = await supabase.from('coaches').select('id').eq('user_id', user.id).maybeSingle();
        if (!coach) return;

        const { data: sessionRows } = await supabase
          .from('coaching_sessions')
          .select('id, session_date, session_analysis, coaching_clients(full_name)')
          .eq('coach_id', coach.id)
          .eq('analysis_status', 'complete')
          .not('session_analysis', 'is', null);

        if (!sessionRows) return;

        const extracted: QuestionEntry[] = [];
        for (const row of sessionRows as unknown as (CoachingSession & { coaching_clients: { full_name: string } | null })[]) {
          if (!row.session_analysis) continue;
          const clientName = row.coaching_clients?.full_name || 'Unknown Client';
          const entries = extractQuestionsFromAnalysis(row.session_analysis, clientName, row.session_date);
          extracted.push(...entries);
        }

        setAllEntries(extracted);
      } finally {
        setLoading(false);
      }
    })();
  }, [isUnlocked]);

  const categories = useMemo(() => {
    if (!isUnlocked) return [];
    const filtered = search
      ? allEntries.filter(e => e.question.toLowerCase().includes(search.toLowerCase()) || e.clientName.toLowerCase().includes(search.toLowerCase()))
      : allEntries;
    return groupByCategory(filtered);
  }, [allEntries, search, isUnlocked]);

  const totalCount = allEntries.length;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-80">Question Bank</h1>
            <p className="text-sm text-gray-50 mt-1">
              {isUnlocked
                ? 'Coaching questions generated from your clients\' session analyses — searchable and copyable.'
                : 'A library of coach questions — categorised by type. After unlock, questions will be generated directly from your client\'s session language.'}
            </p>
          </div>
          {!isUnlocked && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-10 text-orange-70 text-xs font-semibold flex-shrink-0">
              Demo Preview
            </span>
          )}
        </div>
      </motion.div>

      {isUnlocked && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="flex items-center gap-3"
        >
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-40 pointer-events-none" />
            <input
              type="text"
              placeholder="Search questions…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-20 rounded-xl text-sm text-gray-70 placeholder-gray-40 focus:outline-none focus:ring-2 focus:ring-success-30"
            />
          </div>
          {totalCount > 0 && (
            <span className="text-xs text-gray-40">{totalCount} question{totalCount !== 1 ? 's' : ''} from {categories.length} categor{categories.length !== 1 ? 'ies' : 'y'}</span>
          )}
        </motion.div>
      )}

      {isUnlocked && loading && (
        <div className="flex items-center gap-3 p-8 justify-center text-gray-40">
          <div className="w-5 h-5 border-2 border-gray-20 border-t-success-40 rounded-full animate-spin" />
          <span className="text-sm">Loading questions…</span>
        </div>
      )}

      {isUnlocked && !loading && categories.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center gap-3 py-16 text-center"
        >
          <div className="w-12 h-12 rounded-2xl bg-gray-10 flex items-center justify-center">
            <Brain className="w-6 h-6 text-gray-30" />
          </div>
          <p className="text-sm font-semibold text-gray-60">
            {search ? 'No questions match your search.' : 'No session analyses yet.'}
          </p>
          <p className="text-xs text-gray-40 max-w-xs">
            {search ? 'Try a different search term.' : 'Upload and analyse a session to populate your question bank.'}
          </p>
        </motion.div>
      )}

      {isUnlocked && !loading && categories.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4">
          {categories.map((cat, i) => (
            <motion.div
              key={cat.label}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.04 + i * 0.03 }}
              className="bg-white rounded-2xl border border-gray-20 overflow-hidden"
            >
              <div className="px-5 py-3 border-b border-gray-10 flex items-center justify-between">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${cat.color}`}>
                  <BookOpen className="w-3 h-3" />
                  {cat.label}
                </span>
                <span className="text-xs text-gray-40">{cat.questions.length}</span>
              </div>
              <ul className="px-4 py-3 divide-y divide-gray-5">
                {cat.questions.map((q, j) => (
                  <QuestionItem
                    key={j}
                    question={q.question}
                    clientName={q.clientName}
                    sessionDate={q.sessionDate}
                    isUnlocked={isUnlocked}
                  />
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      )}

      {/* Static demo — shown when locked */}
      {!isUnlocked && (
        <div className="grid md:grid-cols-2 gap-4">
          {STATIC_CATEGORIES.map((cat, i) => (
            <motion.div
              key={cat.label}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 + i * 0.04 }}
              className="bg-white rounded-2xl border border-gray-20 overflow-hidden"
            >
              <div className="px-5 py-3 border-b border-gray-10">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${cat.color}`}>
                  <BookOpen className="w-3 h-3" />
                  {cat.label}
                </span>
              </div>
              <ul className="p-4 space-y-2">
                {cat.questions.map((q, j) => (
                  <li key={j} className="flex items-start gap-2.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-success-40 mt-2 flex-shrink-0" />
                    <p className="text-sm text-gray-60 italic leading-relaxed">{q}</p>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
